import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { BillHospOrderDTO, BillHospOrderItemDTO } from '../../dto/budget/BillHospOrdersSummaryDTO';
import { formatMoney } from '../../utils/money';
import { formatDateOnly } from '../../utils/date';
import BenefitOrderStateTag from './BenefitOrderStateTag';

/** Línea de la tabla con los datos de su grupo, para la cabecera y el subtotal de cada tipo de ítem. */
interface OrderItemRow extends BillHospOrderItemDTO {
    groupKey: string;
    groupName: string;
    groupSubtotal: number;
    groupFeeSplit: boolean;
}

interface BillHospOrderSectionProps {
    order: BillHospOrderDTO;
    /** Abre el diálogo del PDF de la orden. */
    onDownloadPdf: (benefitOrderId: number) => void;
}

/** Columnas de la tabla antes del total: el subtotal y el total de la orden van alineados con la última. */
const COLUMNS_BEFORE_TOTAL = 6;

/** Cantidad de la línea: hasta 6 decimales, como las unidades de la factura (medicación cobrada por dosis). */
function formatQuantity(quantity: number | null): string {
    return quantity == null ? '—' : quantity.toLocaleString('es-AR', { maximumFractionDigits: 6 });
}

/** Una orden de prestación (card de Novarium): emisor, destino, fecha y sus ítems agrupados por tipo con su subtotal. */
export default function BillHospOrderSection({ order, onDownloadPdf }: BillHospOrderSectionProps) {
    const titleId = `nv-order-${order.benefitOrder}`;
    const rows: OrderItemRow[] = order.groups.flatMap(group => group.items.map(item => ({
        ...item,
        groupKey: String(group.itemType ?? 'other'),
        groupName: group.itemTypeName,
        groupSubtotal: group.subtotal,
        groupFeeSplit: group.feeSplit,
    })));

    const fields = [
        { label: 'Emisor', value: order.issuerName },
        { label: 'Destino', value: order.destinationName },
        { label: 'Fecha', value: formatDateOnly(order.date) },
    ];

    const footer = (
        <ColumnGroup>
            <Row>
                <Column footer="Total de la orden" colSpan={COLUMNS_BEFORE_TOTAL} />
                <Column footer={formatMoney(order.total)} align="right" className="nv-col-money" />
            </Row>
        </ColumnGroup>
    );

    return (
        <section className={`nv-card nv-order${order.nulled ? ' nv-order-nulled' : ''}`} aria-labelledby={titleId}>
            <div className="nv-section-head">
                <div className="nv-row-8">
                    <h2 id={titleId} className="nv-section-title">
                        Orden N.° {order.benefitOrder}{order.payerTypeName ? ` · ${order.payerTypeName}` : ''}
                    </h2>
                    <BenefitOrderStateTag state={order.state} stateName={order.stateName} />
                </div>
                <Button
                    type="button"
                    text
                    className="nv-icon nv-tip"
                    icon="pi pi-download"
                    aria-label={`Descargar PDF de la orden N.° ${order.benefitOrder}`}
                    data-pr-tooltip="Descargar PDF"
                    onClick={() => onDownloadPdf(order.benefitOrder)}
                />
            </div>
            {order.nulled && <p className="nv-help">La orden está anulada: no suma en los totales.</p>}

            <div className="nv-form-row">
                {fields.map(f => (
                    <div key={f.label} className="nv-ro nv-w-m">
                        <span className="nv-ro-label">{f.label}</span>
                        <span className="nv-ro-value nv-truncate">{f.value || '—'}</span>
                    </div>
                ))}
            </div>

            <DataTable
                value={rows}
                dataKey="benefitOrderDetail"
                className="nv-load-table nv-order-table"
                scrollable
                scrollHeight="580px"
                rowGroupMode="subheader"
                groupRowsBy="groupKey"
                rowGroupHeaderTemplate={(row: OrderItemRow) => <span className="nv-strong">{row.groupName}</span>}
                rowGroupFooterTemplate={(row: OrderItemRow) => (
                    <>
                        <td colSpan={COLUMNS_BEFORE_TOTAL} className="nv-group-subtotal-label">Subtotal {row.groupName.toLowerCase()}</td>
                        <td className="nv-col-money nv-group-subtotal-value">{formatMoney(row.groupSubtotal)}</td>
                    </>
                )}
                footerColumnGroup={rows.length > 0 ? footer : undefined}
                emptyMessage="La orden no tiene ítems."
            >
                <Column header="Código" body={(row: OrderItemRow) => row.code || '—'} className="nv-col-date" />
                <Column header="Descripción" body={(row: OrderItemRow) => <span className="nv-truncate">{row.description || '—'}</span>} className="nv-col-item" />
                <Column header="Cantidad" body={(row: OrderItemRow) => formatQuantity(row.quantity)} align="right" alignHeader="right" className="nv-col-qty" />
                <Column header="Valor unitario" body={(row: OrderItemRow) => formatMoney(row.unitPrice)} align="right" alignHeader="right" className="nv-col-money" />
                {/* Honorarios y derechos solo en las prácticas: el resto es todo derechos */}
                <Column header="Honorarios" body={(row: OrderItemRow) => row.groupFeeSplit ? formatMoney(row.medicalAmount) : '—'} align="right" alignHeader="right" className="nv-col-money" />
                <Column header="Derechos" body={(row: OrderItemRow) => row.groupFeeSplit ? formatMoney(row.expensesAmount) : '—'} align="right" alignHeader="right" className="nv-col-money" />
                <Column header="Total" body={(row: OrderItemRow) => <span className="nv-strong">{formatMoney(row.total)}</span>} align="right" alignHeader="right" className="nv-col-money" />
            </DataTable>
        </section>
    );
}
